'use client';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useT } from '../../i18n';

/**
 * FAQ 區塊（GEO 優化）：問句標題 + 每題一段直接答案——AI 引擎最高引用率的格式。
 * 與 index.html 內 FAQPage JSON-LD 同步（可見文字 = 結構化資料）。
 * 署名行提供 E-E-A-T 訊號（作者 + 更新時間）。
 */
const FaqSection = () => {
  const t = useT();
  const [openIndex, setOpenIndex] = useState<number>(0);

  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
  ];

  return (
    <section id="faq" className="py-12 lg:py-16 relative noise-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-jkd-gray-100 text-jkd-gold text-sm font-mono tracking-widest mb-4 rounded">
            {t('faq.tag')}
          </span>
          <h2 className="section-title text-white">
            <span className="text-gradient-gold">{t('faq.title')}</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            {t('faq.subtitle')}
          </p>
          <div className="divider-gold mx-auto mt-6" />
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`card-dark transition-all duration-300 ${isOpen ? 'border-jkd-gold/50' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="w-full flex items-center justify-between gap-4 text-left py-4 px-5"
                  aria-expanded={isOpen}
                >
                  <h3 className="text-base font-bold text-white">{faq.q}</h3>
                  <ChevronDown
                    className={`w-5 h-5 text-jkd-gold flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-gray-300 text-sm leading-relaxed px-5 pb-5">{faq.a}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* E-E-A-T byline（作者 + 更新時間） */}
        <p className="author mt-8 text-center text-gray-500 text-xs">
          {t('faq.author')} · <time dateTime="2026-09">{t('faq.updated')}</time>
        </p>
      </div>
    </section>
  );
};

export default FaqSection;
